import Swal from 'sweetalert2';
import { transformAsCurrency } from '~/utils/functions';
import { formatarCNPJ } from '../utils/functions';
import { validaTipoItemNcmDepartamento } from '../../../utils/validaTipoItemNcmDepartamento';
import { validateANP } from './validateANP';

type Params = {
  dataForm: any;
  flags: any;
  formProduto: any;
  masterDetail: any;
  mainLoja: any;
  lojas: any;
  hasVariacaoGrade: boolean;
  itemsAbaGrade: any[];
  isUpdate: boolean;
};

type ValidationReturn = {
  valid: boolean;
  message?: string;
  field?: string;
  tab?: string;
  tabGeral?: string;
  extra?: {
    isGtinplu?: boolean;
    forceClearGrade?: boolean;
    forceRestoreGrade?: boolean;
  };
};

export async function validForSubmit({
  dataForm,
  flags,
  formProduto,
  masterDetail,
  mainLoja,
  lojas,
  hasVariacaoGrade,
  itemsAbaGrade,
}: Params): Promise<ValidationReturn> {
  // =========================
  // ANP
  // =========================
  const anp = validateANP(dataForm);
  if (!anp.valid) return anp;

  // =========================
  // DERIVADOS
  // =========================
  const master = masterDetail.find(
    (i: any) => i.obj_name === 'produtos-derivados',
  );

  if (master) {
    const { itens: derivados } = master;
    const qtdItens = derivados.insert.length + derivados.update.length;

    if (qtdItens > 0) {
      const peso = transformAsCurrency(dataForm.val_peso_emb);
      if (peso <= 0) {
        return {
          valid: false,
          message: 'Produto possui derivado(s). Informe o Peso Líquido.',
          field: 'val_peso_emb',
          tab: 'geral',
        };
      }
    }
  }

  // =========================
  // DESCRIÇÃO REDUZIDA
  // =========================
  if (!dataForm.des_reduzida && dataForm.des_produto) {
    const reduzida = dataForm.des_produto.slice(0, 50);
    dataForm.des_reduzida = reduzida;
  }

  // =========================
  // PIS/COFINS
  // =========================
  const tipoNaoPisCofinsInvalido =
    flags.flg_nao_pis_cofins &&
    (dataForm.tipo_nao_pis_cofins?.value === -1 ||
      dataForm.tipo_nao_pis_cofins?.value === undefined);

  const tipoNaoPisCofinsIsento =
    Number(dataForm.tipo_nao_pis_cofins?.value) === 5;

  const codSpedInvalido =
    flags.flg_nao_pis_cofins &&
    !tipoNaoPisCofinsIsento &&
    !dataForm.cod_sped;

  if (tipoNaoPisCofinsInvalido && codSpedInvalido) {
    return {
      valid: false,
      tabGeral: 'pisCofins',
      field: 'tipo_nao_pis_cofins',
      message: 'Preencha os campos obrigatórios do PIS/COFINS',
    };
  }

  if (tipoNaoPisCofinsInvalido) {
    return {
      valid: false,
      tabGeral: 'pisCofins',
      field: 'tipo_nao_pis_cofins',
      message: 'Tipo não PIS/COFINS obrigatório',
    };
  }

  if (codSpedInvalido) {
    return {
      valid: false,
      tabGeral: 'pisCofins',
      field: 'cod_sped',
      message: 'Código SPED obrigatório',
    };
  }

  // =========================
  // NCM / DEPARTAMENTO
  // =========================
  if (await validaTipoItemNcmDepartamento(dataForm, mainLoja, lojas)) {
    return { valid: false };
  }

  // =========================
  // BALANÇA
  // =========================
  if (flags.flg_envia_balanca) {
    if (Number(dataForm.cod_produto) > Number('000000999999')) {
      return {
        valid: false,
        message: 'Produto de balança com Digíto Alto!',
      };
    }
  }

  // =========================
  // CÓDIGO PRODUTO
  // =========================
  if (!dataForm.cod_produto) {
    return {
      valid: false,
      tab: 'geral',
      message: 'Código do produto inválido.',
    };
  }

  // =========================
  // NCM VINCULADO
  // =========================
  if (
    dataForm.des_produto &&
    dataForm.des_reduzida &&
    dataForm.cod_departamento?.value &&
    !dataForm.cod_ncm
  ) {
    return {
      valid: false,
      message: 'Vincule um NCM ao produto',
      tab: 'geral',
    };
  }

  // =========================
  // PESO
  // =========================
  if (transformAsCurrency(dataForm.val_peso) <= 0) {
    return {
      valid: false,
      message: 'Peso deve ser informado.',
    };
  }

  // =========================
  // FORNECEDORES
  // =========================
  if (dataForm.fornecedores) {
    const hasInvalidFornecedor = dataForm.fornecedores.find(
      (fornecedor: any) => fornecedor.Unidades.unidades.length < 1,
    );

    if (hasInvalidFornecedor) {
      return {
        valid: false,
        message: `O Fornecedor com CNPJ: ${formatarCNPJ(
          hasInvalidFornecedor?.num_cpf_cnpj?.value,
        )} está sem embalagem vinculada, é necessário o vínculo de ao menos uma embalagem`,
      };
    }
  }

  // =========================
  // GTIN / PLU
  // =========================
  if (formProduto.Gtins.gtins.length < 1) {
    const { isConfirmed } = await Swal.fire({
      title:
        'Código de barras não inserido, deseja criar um código de barras a partir do PLU?',
      showDenyButton: true,
      confirmButtonText: 'SIM',
      denyButtonText: 'Não',
    });

    if (!isConfirmed) {
      return {
        valid: false,
        message: 'O Produto deve conter ao menos um GTIN vinculado.',
      };
    }

    return {
      valid: true,
      extra: { isGtinplu: true },
    };
  }

  // =========================
  // GRADE / VARIAÇÃO
  // =========================
  if (!dataForm.duplicaForm && !hasVariacaoGrade && itemsAbaGrade.length > 0) {
    const { isConfirmed } = await Swal.fire({
      title:
        'Há itens na grade, mas o produto foi redefinido como não tendo variações.',
      text: 'Grade será perdida, deseja realmente continuar?',
      icon: 'info',
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonColor: '#28A745',
      cancelButtonColor: '#DC3545',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    });

    if (!isConfirmed) {
      return {
        valid: false,
        extra: { forceRestoreGrade: true },
      };
    }

    return {
      valid: true,
      extra: { forceClearGrade: true },
    };
  }

  return { valid: true };
}
